import { useRouterState } from '@tanstack/react-router'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import {
  SelectionContext,
  type SelectableKind,
  type Selection,
} from '~/components/selection-context'

/** What is selected, and the page it was selected on — the two are one fact. */
interface Held {
  readonly page: string
  readonly selection: Selection | null
}

/**
 * Holds what is selected for as long as the page it was selected on is the page being read.
 * A selection is about the things in front of you, so leaving takes it with you rather than
 * carrying a count of tiles nobody can see any more into the next page's header.
 */
export function SelectionProvider({ children }: { children: ReactNode }) {
  const page = useRouterState({ select: (state) => state.location.pathname })
  const [held, setHeld] = useState<Held>({ page, selection: null })
  // Put down as the new page renders rather than after it has: an effect would paint the
  // page under a header still counting the last page's tiles.
  if (held.page !== page) {
    setHeld({ page, selection: null })
  }

  const toggle = useCallback((kind: SelectableKind, id: string) => {
    setHeld((was) => {
      // A selection of one kind is never joined by another: pressing a workout while sets are
      // selected does nothing at all, so nothing of the other kind ever reaches this.
      const open = was.selection
      if (open?.kind !== kind) {
        return { ...was, selection: { kind, ids: [id] } }
      }
      const left = open.ids.filter((taken) => taken !== id)
      if (left.length === open.ids.length) {
        return { ...was, selection: { kind, ids: [...open.ids, id] } }
      }
      // Taking the last thing out closes the selection rather than leaving an empty one open.
      return { ...was, selection: left.length === 0 ? null : { kind, ids: left } }
    })
  }, [])

  const clear = useCallback(() => {
    setHeld((was) => (was.selection === null ? was : { ...was, selection: null }))
  }, [])

  const [actions, setActions] = useState<ReactNode>(null)

  const handle = useMemo(
    () => ({
      selection: held.page === page ? held.selection : null,
      toggle,
      clear,
      actions,
      setActions,
    }),
    [held, page, toggle, clear, actions],
  )

  return <SelectionContext.Provider value={handle}>{children}</SelectionContext.Provider>
}
